import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Space, Typography, Select, DatePicker, Tag, Button,
  Row, Col, Alert, Spin, Tabs, InputNumber, ConfigProvider, Checkbox,
} from 'antd';
import {
  ArrowLeftOutlined, EyeOutlined, BarChartOutlined,
} from '@ant-design/icons';
import BusinessUnitReport from '../BusinessUnitReport';
import './NewReportPage.css';
import dayjs from 'dayjs';

const { Text } = Typography;

const BU_THEME = {
  token: { colorPrimary: '#2E86AB', colorLink: '#2E86AB', borderRadius: 6 },
  components: { Card: { headerFontSize: 15 } },
};

/**
 * NewBUReportPage — tab-based Business Unit report builder.
 * Mirrors NewReportPage layout: single-screen setup + preview tab.
 *
 * Props (from Reports.js buStore):
 *   store   — bu* state + handlers
 *   onBack  — () => void
 */
const NewBUReportPage = ({ store, onBack }) => {
  const {
    reportName, setReportName,
    selectedOrganization, setSelectedOrganization,
    allOrganizations,
    selectedMonth, setSelectedMonth,
    campaigns, campaignsLoading,
    selectedCampaignIds, setSelectedCampaignIds,
    reportData, setReportData,
    reportLoading,
    completionBenchmark, setCompletionBenchmark,
    scoreBenchmark, setScoreBenchmark,
    onGenerate,
  } = store;

  const [activeTab, setActiveTab] = useState('setup');
  const [dirty, setDirty] = useState(false);
  const configSnapshotRef = useRef(null);

  const reportReady = !!reportData;

  const configFingerprint = useCallback(() => JSON.stringify({
    org: selectedOrganization,
    month: selectedMonth?.valueOf(),
    cr: completionBenchmark,
    sc: scoreBenchmark,
    campaigns: [...selectedCampaignIds].sort(),
  }), [selectedOrganization, selectedMonth, completionBenchmark, scoreBenchmark, selectedCampaignIds]);

  // Track dirty state
  useEffect(() => {
    const fp = configFingerprint();
    if (configSnapshotRef.current === null) {
      configSnapshotRef.current = fp;
      return;
    }
    if (fp !== configSnapshotRef.current) setDirty(true);
  }, [configFingerprint]);

  // Reset dirty when report is generated
  useEffect(() => {
    if (reportReady) {
      setDirty(false);
      configSnapshotRef.current = configFingerprint();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  // Auto-switch to preview after generation
  useEffect(() => {
    if (reportReady && activeTab === 'setup') setActiveTab('preview');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportReady]);

  const selectedOrg = allOrganizations.find(o => o.id === selectedOrganization);
  const canGenerate = !!(selectedOrganization && selectedMonth && selectedCampaignIds.length > 0);

  const statusText = !selectedOrganization
    ? 'Select an organisation to start'
    : !selectedMonth
    ? 'Select the report month'
    : campaignsLoading
    ? 'Loading campaigns…'
    : campaigns.length === 0
    ? 'No campaigns found for this org & month'
    : selectedCampaignIds.length === 0
    ? 'Select at least one campaign to include'
    : reportReady
    ? 'Report ready — regenerate or adjust benchmarks'
    : `${selectedCampaignIds.length} of ${campaigns.length} campaign(s) selected — click Generate`;

  const setupTabLabel = (
    <Space size={6}>
      <span className={`step-tag ${reportReady ? 'done' : canGenerate ? '' : 'idle'}`}>1</span>
      Setup
    </Space>
  );

  const previewTabLabel = (
    <Space size={6}>
      <span className={`step-tag ${reportReady ? 'done' : 'idle'}`}>2</span>
      Preview
      {reportReady && <Tag color="green" style={{ fontSize: 10, margin: 0 }}>Ready</Tag>}
    </Space>
  );

  return (
    <ConfigProvider theme={BU_THEME}>
      {/* Dirty notice */}
      {dirty && reportReady && (
        <div className="dirty-notice">
          <span>&#9888;</span>
          Settings changed since last generation — regenerate to update the preview.
        </div>
      )}

      <Card
        title="Business Unit Report"
        extra={<Button icon={<ArrowLeftOutlined />} onClick={onBack}>Back to Reports</Button>}
        bodyStyle={{ paddingTop: 8 }}
      >
        <Tabs
          className="nr-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'setup',
              label: setupTabLabel,
              children: (
                <BUSetupPanel
                  store={store}
                  canGenerate={canGenerate}
                  reportReady={reportReady}
                  dirty={dirty}
                  statusText={statusText}
                  selectedOrg={selectedOrg}
                />
              ),
            },
            {
              key: 'preview',
              label: previewTabLabel,
              children: (
                <BUPreviewPanel
                  reportData={reportData}
                  reportLoading={reportLoading}
                  reportReady={reportReady}
                  dirty={dirty}
                  onGenerate={onGenerate}
                  defaultReportName={reportName}
                  completionBenchmark={completionBenchmark}
                  scoreBenchmark={scoreBenchmark}
                />
              ),
            },
          ]}
        />
      </Card>
    </ConfigProvider>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* BUSetupPanel — left: config, right: sticky rail                            */
/* ─────────────────────────────────────────────────────────────────────────── */
const BUSetupPanel = ({ store, canGenerate, reportReady, dirty, statusText, selectedOrg }) => {
  const {
    selectedOrganization, setSelectedOrganization,
    allOrganizations,
    selectedMonth, setSelectedMonth,
    campaigns, campaignsLoading,
    selectedCampaignIds, setSelectedCampaignIds,
    reportLoading,
    setReportData,
    completionBenchmark, setCompletionBenchmark,
    scoreBenchmark, setScoreBenchmark,
    onGenerate,
  } = store;

  const primaryLabel = reportReady
    ? (dirty ? 'Regenerate Report' : 'Regenerate Report')
    : 'Generate Report';

  return (
    <Row gutter={24} align="top">
      {/* ── Left: config ── */}
      <Col xs={24} lg={16}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>

          {/* Organisation & Month */}
          <div className="nr-section">
            <Text className="nr-section-title">Select Data</Text>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={10}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Organisation</Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select organisation"
                  value={selectedOrganization}
                  onChange={v => { setSelectedOrganization(v); setReportData(null); }}
                  showSearch
                  filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={allOrganizations.map(o => ({ value: o.id, label: o.name }))}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Month</Text>
                <DatePicker
                  picker="month"
                  style={{ width: '100%' }}
                  value={selectedMonth}
                  onChange={d => { setSelectedMonth(d); setReportData(null); }}
                  placeholder="Select month"
                  disabledDate={c => c && c > dayjs().endOf('month')}
                />
              </Col>
            </Row>

            {/* Campaign info */}
            {selectedOrganization && selectedMonth && (
              <div style={{ marginTop: 12 }}>
                {campaignsLoading ? (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <Spin size="small" style={{ marginRight: 8 }} />Loading campaigns…
                  </Text>
                ) : campaigns.length === 0 ? (
                  <Alert
                    message="No campaigns found"
                    description={`No campaigns targeted ${selectedOrg?.name} during ${selectedMonth?.format('MMMM YYYY')}`}
                    type="warning" showIcon
                  />
                ) : (
                  <Alert
                    message={`${campaigns.length} campaign(s) found — untick any to leave out of the report`}
                    description={
                      <div>
                        <div style={{ marginBottom: 8 }}>
                          <Checkbox
                            checked={selectedCampaignIds.length === campaigns.length}
                            indeterminate={selectedCampaignIds.length > 0 && selectedCampaignIds.length < campaigns.length}
                            onChange={(e) => setSelectedCampaignIds(e.target.checked ? campaigns.map(c => c._id) : [])}
                          >
                            Select all
                          </Checkbox>
                        </div>
                        <Checkbox.Group
                          style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                          value={selectedCampaignIds}
                          onChange={setSelectedCampaignIds}
                        >
                          {campaigns.map(c => (
                            <Checkbox key={c._id} value={c._id}>{c.name}</Checkbox>
                          ))}
                        </Checkbox.Group>
                      </div>
                    }
                    type="info" showIcon
                  />
                )}
              </div>
            )}
          </div>

          {/* Benchmarks */}
          <div className="nr-section">
            <Text className="nr-section-title">Benchmarks</Text>
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={8}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Completion Rate Target</Text>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0} max={100}
                  value={completionBenchmark}
                  onChange={v => setCompletionBenchmark(v || 0)}
                  formatter={v => `${v}%`}
                  parser={v => v.replace('%', '')}
                />
              </Col>
              <Col xs={12} sm={8}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Score Target</Text>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0} max={100}
                  value={scoreBenchmark}
                  onChange={v => setScoreBenchmark(v || 0)}
                  formatter={v => `${v}%`}
                  parser={v => v.replace('%', '')}
                />
              </Col>
            </Row>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
              These thresholds appear on the report's benchmark pills and performance indicators.
            </Text>
          </div>

        </Space>
      </Col>

      {/* ── Right: sticky rail ── */}
      <Col xs={24} lg={8}>
        <div className="rail-sticky">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>

            <div className="nr-section">
              <Text className="nr-section-title">Generate</Text>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
                {statusText}
              </Text>
              <Button
                type="primary"
                block
                size="large"
                disabled={!canGenerate}
                loading={reportLoading}
                onClick={onGenerate}
              >
                {primaryLabel}
              </Button>
            </div>

            {(selectedOrganization || selectedMonth) && (
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', background: '#fafafa' }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {[
                    ['Organisation', selectedOrg?.name || '—'],
                    ['Month', selectedMonth?.format('MMMM YYYY') || '—'],
                    ['Campaigns', campaigns.length ? `${selectedCampaignIds.length} / ${campaigns.length}` : '—'],
                    ['CR Benchmark', `${completionBenchmark}%`],
                    ['Score Benchmark', `${scoreBenchmark}%`],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <Text type="secondary">{label}</Text>
                      <Text>{val}</Text>
                    </div>
                  ))}
                </Space>
              </div>
            )}

          </Space>
        </div>
      </Col>
    </Row>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* BUPreviewPanel — shows BusinessUnitReport inline                           */
/* ─────────────────────────────────────────────────────────────────────────── */
const BUPreviewPanel = ({
  reportData, reportLoading, reportReady,
  dirty, onGenerate, defaultReportName,
  completionBenchmark, scoreBenchmark,
}) => {
  if (!reportReady) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: '#bbb' }}>
        <BarChartOutlined style={{ fontSize: 40, marginBottom: 12 }} />
        <div style={{ fontSize: 14 }}>
          {dirty
            ? 'Settings changed — regenerate the report to see a preview.'
            : 'Complete setup and generate the report to see the preview here.'}
        </div>
        {dirty && (
          <Button type="primary" style={{ marginTop: 16 }} loading={reportLoading} onClick={onGenerate}>
            Regenerate Report
          </Button>
        )}
      </div>
    );
  }

  if (reportLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <div style={{ marginTop: 12, color: '#6b7280', fontSize: 13 }}>Generating report…</div>
      </div>
    );
  }

  const overview = reportData?.overview || {};

  return (
    <>
      {/* Preview header bar */}
      <div style={{
        background: 'linear-gradient(135deg, #1A3A6B 0%, #267a96 100%)',
        borderRadius: '6px 6px 0 0',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
            TapLearnGo
          </div>
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)' }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
            {defaultReportName || 'Business Unit Report'}
          </div>
        </div>
        {dirty && (
          <Tag style={{ background: 'rgba(250,173,20,0.2)', color: '#ffe58f', border: '1px solid rgba(255,229,143,0.4)', fontSize: 11 }}>
            &#9888; Settings changed
          </Tag>
        )}
      </div>

      {/* KPI strip */}
      <div style={{ background: '#fff', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
        <div className="ps-kpis">
          {[
            { label: 'Completion Rate', value: overview.completionRate != null ? `${Math.round(overview.completionRate)}%` : '—' },
            { label: 'Avg Score', value: overview.averageScore != null ? `${Math.round(overview.averageScore)}%` : '—' },
            { label: 'Participants', value: overview.totalParticipants ?? '—' },
            { label: 'CR Benchmark', value: `${completionBenchmark}%` },
          ].map(kpi => (
            <div key={kpi.label} className="ps-kpi">
              <div className="ps-kpi__label">{kpi.label}</div>
              <div className="ps-kpi__value">{kpi.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Report body */}
      <div className="preview-shell">
        <BusinessUnitReport
          data={reportData}
          defaultReportName={defaultReportName}
          benchmark={completionBenchmark || scoreBenchmark || 70}
        />
      </div>
    </>
  );
};

export default NewBUReportPage;
